// The home modal's glyphs: one shape per choice, drawn from the sprout and
// from sound rather than from a stock icon set. Stroke in currentColor, an
// accent in the primary mint.
//
// They move in time with the Flip (styles/globals.css, `.gl-*`): idle, the
// accent beats on the quarter note; hovered or focused (the tile is a
// `group`), the whole glyph plays — the waveform runs, the record spins up,
// the rings ring out. `--beat` (seconds per beat, from the Flip's tempo) is
// set by whatever holds the tiles. Nothing moves under reduced motion.

import type { ReactNode, SVGProps } from "react";

function Glyph({ children, ...props }: SVGProps<SVGSVGElement> & { children: ReactNode }) {
  return (
    <svg viewBox="0 0 40 40" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" aria-hidden {...props}>
      {children}
    </svg>
  );
}

/** Plant a seed: the logo's two leaves over the seed. */
export function SproutGlyph(props: SVGProps<SVGSVGElement>) {
  return (
    <Glyph {...props}>
      <path d="M20 27V19" />
      <g className="gl-sway">
        <path d="M20 19c0-6-4-9-10-9 0 6 4 9 10 9Z" fill="var(--primary)" stroke="var(--primary)" />
        <path d="M20 19c0-6 4-9 10-9 0 6-4 9-10 9Z" fill="var(--primary)" stroke="var(--primary)" />
      </g>
      <circle cx={20} cy={32} r={2.6} fill="currentColor" stroke="none" />
    </Glyph>
  );
}

/** Solo: one waveform. */
export function SoloGlyph(props: SVGProps<SVGSVGElement>) {
  return (
    <Glyph {...props}>
      <path d="M5 20h4l3-8 5 16 4-12 3 6 3-4 3 2h5" opacity={0.25} />
      <path className="gl-run" pathLength={100} d="M5 20h4l3-8 5 16 4-12 3 6 3-4 3 2h5" stroke="var(--primary)" />
    </Glyph>
  );
}

/** Multiplayer: two waveforms crossing. */
export function MultiplayerGlyph(props: SVGProps<SVGSVGElement>) {
  return (
    <Glyph {...props}>
      <path className="gl-run" pathLength={100} d="M4 22c4 0 5-10 9-10s5 16 9 16 5-12 9-12 4 6 5 6" stroke="var(--primary)" />
      <path className="gl-run gl-reverse" pathLength={100} d="M4 18c4 0 5 8 9 8s5-12 9-12 5 10 9 10 4-4 5-4" opacity={0.55} />
    </Glyph>
  );
}

/** Get inspired: a record, with the light catching it. */
export function RecordGlyph(props: SVGProps<SVGSVGElement>) {
  return (
    <Glyph {...props}>
      <g className="gl-spin">
        <circle cx={20} cy={20} r={14} />
        <circle cx={20} cy={20} r={5} fill="var(--primary)" stroke="var(--primary)" />
        <circle cx={20} cy={20} r={1.2} fill="currentColor" stroke="none" />
        <path d="M10.5 13.5a11 11 0 0 1 6-4.6" opacity={0.6} />
      </g>
    </Glyph>
  );
}

/** Start a jam: the live dot, ringing out. */
export function LiveGlyph(props: SVGProps<SVGSVGElement>) {
  return (
    <Glyph {...props}>
      <circle className="gl-beat" cx={20} cy={20} r={5} fill="var(--primary)" stroke="var(--primary)" />
      <circle className="gl-ring" cx={20} cy={20} r={10} opacity={0.6} />
      <path className="gl-ring gl-late" d="M8.7 8.7a16 16 0 0 0 0 22.6M31.3 8.7a16 16 0 0 1 0 22.6" opacity={0.35} />
    </Glyph>
  );
}

/** Join a jam: the room's people, stacked. */
export function JoinGlyph(props: SVGProps<SVGSVGElement>) {
  return (
    <Glyph {...props}>
      <circle cx={14} cy={20} r={7} />
      <circle className="gl-join" cx={26} cy={20} r={7} fill="var(--primary)" stroke="var(--primary)" />
    </Glyph>
  );
}

/** Your seeds: the sprout, in a pot of its own. */
export function MySeedsGlyph(props: SVGProps<SVGSVGElement>) {
  return (
    <Glyph {...props}>
      <path d="M11 24h18l-2 10H13Z" />
      <path d="M20 24v-6" />
      <path d="M20 18c0-4-3-6-7-6 0 4 3 6 7 6Z" fill="var(--primary)" stroke="var(--primary)" />
      <path d="M20 18c0-4 3-6 7-6 0 4-3 6-7 6Z" fill="var(--primary)" stroke="var(--primary)" />
    </Glyph>
  );
}
